import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import JsonLd from "@/components/feature/JsonLd";
import ArticleTimeline from "./components/ArticleTimeline";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatHeat, getArticleById, getRelatedArticles } from "@/utils/news";

function ArticleDetail() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const related = article ? getRelatedArticles(article, 4) : [];

  useDocumentMeta({
    title: article ? `${article.title} - 瓜圈速报` : "速报不存在 - 瓜圈速报",
    description: article
      ? `${article.summary} 瓜圈速报持续跟进该事件的最新进展与后续回应。`
      : "这条速报可能已被移除，返回瓜圈速报首页查看最新快讯。",
    keywords: article
      ? `${article.tags.join(",")},瓜圈速报,最新进展抢先看`
      : "瓜圈速报,最新进展抢先看",
    canonical: article ? `https://hrbsnws.cn/article/${article.id}` : "https://hrbsnws.cn/",
  });

  if (!article) {
    return (
      <>
        <SiteHeader />
        <main className="pt-28 md:pt-32 pb-20 px-4 md:px-10 min-h-[60vh] flex items-center justify-center">
          <div className="text-center max-w-md">
            <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-3xl" />
            </span>
            <h1 className="mt-6 font-heading text-2xl font-black text-foreground-950">这条速报找不到了</h1>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              可能是链接有误，或者内容已经被收进归档。回到首页看看最新的一手快讯吧。
            </p>
            <Link
              to="/"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.summary,
        image: article.coverImage,
        inLanguage: "zh-CN",
        keywords: article.tags.join(","),
        articleSection: article.categoryName,
        publisher: {
          "@type": "Organization",
          name: "瓜圈速报",
        },
        url: `https://hrbsnws.cn/article/${article.id}`,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://hrbsnws.cn/" },
          {
            "@type": "ListItem",
            position: 2,
            name: article.categoryName,
            item: `https://hrbsnws.cn/category/${article.category}`,
          },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
    ],
  };

  return (
    <>
      <JsonLd data={jsonLd} />
      <SiteHeader />
      <main className="pt-24 md:pt-28">
        <div className="w-full px-4 md:px-10 py-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: article.categoryName, to: `/category/${article.category}` },
              { label: "速报详情" },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-10 pb-16 md:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <article className="lg:col-span-8">
              <div className="flex items-center gap-2 flex-wrap mb-5">
                {article.isBreaking && (
                  <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[11px] font-bold">
                    突发速报
                  </span>
                )}
                <Link
                  to={`/category/${article.category}`}
                  className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-medium cursor-pointer hover:bg-secondary-200 transition-colors"
                >
                  {article.categoryName}
                </Link>
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-primary-600 font-medium">
                  <span className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-fire-line" />
                  </span>
                  {formatHeat(article.heat)} 热度
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-black leading-tight text-foreground-950">
                {article.title}
              </h1>

              <p className="mt-5 text-base md:text-lg leading-relaxed text-foreground-700 border-l-4 border-primary-300 pl-5">
                {article.summary}
              </p>

              <figure className="mt-8">
                <div className="w-full h-64 md:h-[420px] rounded-lg overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    title={`${article.title} - 瓜圈速报`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <figcaption className="mt-3 text-xs text-foreground-500 text-center">
                  配图为速报氛围示意图，非现场实拍
                </figcaption>
              </figure>

              <div className="mt-8 space-y-6">
                {article.body.map((paragraph, idx) => (
                  <p key={idx} className="text-base leading-loose text-foreground-800">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">相关标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-700 text-xs border border-background-200"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 p-5 rounded-lg bg-secondary-50 border border-secondary-200 lg:hidden">
                <p className="text-xs leading-relaxed text-secondary-800">
                  提示：本速报内容为公开信息整理，涉及的人际关系判断请以当事人及官方回应为准。瓜圈速报会持续跟进并更新后续进展。
                </p>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28 space-y-6">
                <ArticleTimeline items={article.timeline} />

                <div className="p-5 rounded-lg bg-accent-50 border border-accent-200">
                  <h3 className="font-heading text-sm font-bold text-accent-950 flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-700">
                      <i className="ri-error-warning-line" />
                    </span>
                    看瓜小提示
                  </h3>
                  <p className="text-xs leading-relaxed text-accent-900">
                    本速报内容为公开信息整理，涉及的人际关系判断请以当事人及官方回应为准。理性吃瓜，不传播未经核实的画面。
                  </p>
                </div>

                <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-4 border-b border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                      <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                        <i className="ri-links-line" />
                      </span>
                      相关速报
                    </h3>
                  </div>
                  <ul className="divide-y divide-background-200">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/article/${item.id}`}
                          className="flex gap-3 px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className="w-16 h-16 rounded-md overflow-hidden shrink-0">
                            <img
                              src={item.coverImage}
                              alt={item.title}
                              title={`${item.title} - 瓜圈速报`}
                              className="w-full h-full object-cover object-top"
                            />
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-medium text-foreground-900 leading-snug line-clamp-2">
                              {item.title}
                            </span>
                            <span className="mt-1.5 block text-xs text-foreground-500">{item.publishedAt}</span>
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}

export default ArticleDetail;